import {Injectable} from '@angular/core';
import {Storage} from '@ionic/storage-angular';
import {IQuestionnaire, Questionnaire} from './questionnaire/questionnaireClassesAndEnums';
import {IUser, User} from './DataModels/User';
import {Food, IFood} from './DataModels/food';
import {ILastTrackerUsed, LastTrackerUsed} from './DataModels/LastTrackerUsed';
import {IUsedFoodStorageEntry} from './food.service';
import {Subject} from 'rxjs';
import {filter, take} from 'rxjs/operators';
import {SqlStorageDataType, SqlStorageService} from './sql-storage.service';
import {PlatformHelperService} from './helpers/platform-helper.service';
import {versionStringToAppVersion} from './DataModels/AppVersion';


@Injectable({
  providedIn: 'root'
})
export class StorageService {

  public foods: Food[];
  public appIntroWasShown: boolean;
  public versionHintsDismissed: string[];

  private _storage: Storage | null = null;
  private storageReady = false;
  private storageReadySubject = new Subject<boolean>();

  constructor(private storage: Storage, private sqlStorage: SqlStorageService, private platformHelper: PlatformHelperService) {
    // Wait until storage is ready before performing the first requests on the storage

    this.init().then(() => {

      // Initialize testing foods
      this.initializeFoods();
      // Initialize if app intro was shown already
      this.initializeAppIntroWasShown();
      // Initialize which version hints were dismissed by the user and shouldn't be displayed anymore
      this.initializeVersionHintsDismissed();

  });

  }

  async storeUser(u: User, changeId?: string): Promise<void> {
    console.log('Starting to update user in storage...');
    if (changeId) {
      u.lastChangedAt = new Date();
      u.lastChangeId = changeId;
    }
    await this.set('user', u.toInterface(), SqlStorageDataType.jsonBlob);
    console.log('Updated user successfully in storage');
  }

  getUser(): Promise<IUser> {
    return this.get('user');
  }

  deleteUser() {
    this.remove('user').then();
  }

  storeAppIntroWasShown(wasShown: boolean) {
    this.appIntroWasShown = wasShown;
    if (wasShown) {
      this.set('appIntroWasShown', wasShown, SqlStorageDataType.boolean).then();
    } else {
      this.remove('appIntroWasShown').then();
    }
  }

  getAppIntroWasShown(): Promise<boolean> {
    return this.get('appIntroWasShown');
  }

  storeLastTrackerUsed(l: LastTrackerUsed) {
    this.set('lastTrackerUsed', l.toInterface(), SqlStorageDataType.object).then();
  }

  getLastTrackerUsed(): Promise<ILastTrackerUsed> {
    return this.get('lastTrackerUsed');
  }

  storeQuestionnaire(q: Questionnaire) {
    this.set('questionnaire', q.toInterface(), SqlStorageDataType.object).then();
  }

  getQuestionnaire(): Promise<IQuestionnaire> {
    return this.get('questionnaire');
  }

  fetchFoods(): Promise<IFood[]> {
    return fetch('./assets/foods.json').then(res => res.json());
  }

  getFoods(): Food[] {
    if (this.foods === undefined) {
      this.initializeFoods();
      return undefined;
    }else{
      return this.foods;
    }
  }

  getFoodById(id: string): Food {
    if (this.foods === undefined) {
      this.initializeFoods();
      return undefined;
    }else{
      return this.foods.find(i => i.id === id);
    }
  }

  storeVersionHintDismissed(version: string): void {
    if (this.checkIfVersionHintAlreadyExists(version)) {
      throw new Error('Tried to add version which already exists: ' + version);
    }
    this.versionHintsDismissed.push(version);
    this.set('versionHintsDismissed', this.versionHintsDismissed, SqlStorageDataType.object).then();
  }

  getLastUsedAppVersion(): Promise<string> {
    return this.get('lastUsedAppVersion');
  }

  storeLastUsedAppVersion(version: string): void {
    this.set('lastUsedAppVersion', version, SqlStorageDataType.string).then();
  }

  getUsedFoods(): Promise<IUsedFoodStorageEntry[]> {
    return this.get('usedFoods');
  }

  storeUsedFoods(usedFoods: IUsedFoodStorageEntry[]): void {
    this.set('usedFoods', usedFoods, SqlStorageDataType.object).then();
  }

  getSpeechToMealDataProtectionAgreement(): Promise<boolean> {
    return this.get('speechToMealDataProtectionAgreement');
  }

  storeSpeechToMealDataProtectionAgreement(value: boolean): void {
    this.set('speechToMealDataProtectionAgreement', value, SqlStorageDataType.boolean).then();
  }

  getStringGeneric(key: string): Promise<string> {
    return this.get(key);
  }

  storeStringGeneric(key: string, value: string): void {
    this.set(key, value, SqlStorageDataType.string).then();
  }

  removeStringGeneric(key: string): void {
    this.remove(key).then();
  }

  private async init(): Promise<void> {
    if (this.platformHelper.isMobile) {
      await this.initSqlStorage();
    } else {
      await this.initLocalStorage();
    }
  }

  private async initLocalStorage(): Promise<void> {
    this._storage = await this.storage.create();
    console.log('Using web storage');
    this.initFinished();
  }

  private async initSqlStorage(): Promise<void> {
    await this.sqlStorage.initDatabase();
    console.log('Using SQL storage');
    const migrationNeeded = await this.checkMigrationToSqlNeeded();
    if (migrationNeeded) {
      await this.migrateToSql();
    } else {
      console.log('No migration needed');
    }
    this.initFinished();
  }

  private initFinished(): void {
    this.storageReady = true;
    this.storageReadySubject.next(true);
  }

  private async checkMigrationToSqlNeeded(): Promise<boolean> {
    console.log('Checking if migration from web storage to SQL is needed...');
    this._storage = await this.storage.create();
    const lastUsedVersionString = await this._storage.get('lastUsedAppVersion');
    if (!lastUsedVersionString) {
      return false;
    }
    return versionStringToAppVersion(lastUsedVersionString).major < 2;
  }

  private async migrateToSql(): Promise<void> {
    console.log('Starting to migrate web storage to SQL...');
    const appIntroWasShown = await this._storage.get('appIntroWasShown');
    if (appIntroWasShown) {
      await this.setForce('appIntroWasShown', appIntroWasShown, SqlStorageDataType.boolean);
    }
    const lastUsedAppVersion = await this._storage.get('lastUsedAppVersion');
    if (lastUsedAppVersion) {
      await this.setForce('lastUsedAppVersion', lastUsedAppVersion, SqlStorageDataType.string);
    }
    const user = await this._storage.get('user');
    if (user) {
      await this.setForce('user', user, SqlStorageDataType.object);
    }
    const lastTrackerUsed = await this._storage.get('lastTrackerUsed');
    if (lastTrackerUsed) {
      await this.setForce('lastTrackerUsed', lastTrackerUsed, SqlStorageDataType.object);
    }
    const questionnaire = await this._storage.get('questionnaire');
    if (questionnaire) {
      await this.setForce('questionnaire', questionnaire, SqlStorageDataType.object);
    }
    const versionHintsDismissed = await this._storage.get('versionHintsDismissed');
    if (versionHintsDismissed) {
      await this.setForce('versionHintsDismissed', versionHintsDismissed, SqlStorageDataType.object);
    }
    const usedFoods = await this._storage.get('usedFoods');
    if (usedFoods) {
      await this.setForce('usedFoods', usedFoods, SqlStorageDataType.object);
    }
    const speechToMealDataProtectionAgreement = await this._storage.get('speechToMealDataProtectionAgreement');
    if (speechToMealDataProtectionAgreement) {
      await this.setForce('speechToMealDataProtectionAgreement', speechToMealDataProtectionAgreement, SqlStorageDataType.boolean);
    }
    await this._storage.clear();
    console.log('Migration finished');
  }

  private get(key: string): Promise<any> {
    if (this.storageReady) {
      return this.getForce(key);
    }
    return this.storageReadySubject.pipe(
        filter(value => value), // Only proceed when storage is ready
        take(1) // Take only the first emitted value and then complete
    ).toPromise().then(() => this.getForce(key));
  }

  private getForce(key: string): Promise<any> {
    if (this.platformHelper.isMobile) {
      return this.sqlStorage.get(key);
    } else {
      return this._storage.get(key);
    }
  }

  private set(key: string, value: any, type: SqlStorageDataType): Promise<void> {
    if (this.storageReady) {
      return this.setForce(key, value, type);
    }
    return this.storageReadySubject.pipe(
        filter(value => value), // Only proceed when storage is ready
        take(1) // Take only the first emitted value and then complete
    ).toPromise().then(() => this.setForce(key, value, type));
  }

  private setForce(key: string, value: any, type: SqlStorageDataType): Promise<any> {
    if (this.platformHelper.isMobile) {
      return this.sqlStorage.set(key, value, type);
    } else {
      return this._storage.set(key, value);
    }
  }

  private remove(key: string): Promise<void> {
    if (this.storageReady) {
      return this.removeForce(key);
    }
    return this.storageReadySubject.pipe(
        filter(value => value), // Only proceed when storage is ready
        take(1) // Take only the first emitted value and then complete
    ).toPromise().then(() => this.removeForce(key));
  }

  private removeForce(key: string): Promise<any> {
    if (this.platformHelper.isMobile) {
      return this.sqlStorage.remove(key);
    } else {
      return this._storage.remove(key);
    }
  }

  private getVersionHintsDismissed(): Promise<string[]> {
    return this.get('versionHintsDismissed');
  }

  private checkIfVersionHintAlreadyExists(version: string): boolean {
    if (!this.versionHintsDismissed || this.versionHintsDismissed.length === 0) { return false; }
    return !!this.versionHintsDismissed.find(v => v === version);
  }

  private initializeFoods() {
    const jsonPromise: Promise<IFood[]> = this.fetchFoods();
    jsonPromise.then((val: IFood[]) => {
        if (val !== undefined) {
          this.foods = [];
          val.forEach(element => {
            this.foods.push(new Food(element));
          });
        }
      });
  }

  private initializeAppIntroWasShown(){
    const wasShownPromise: Promise<boolean> = this.getAppIntroWasShown();
    wasShownPromise.then((val: boolean) => {
      if (val !== undefined){
        this.appIntroWasShown = val;
      }else{
        this.appIntroWasShown = false;
      }
    });
  }

  private initializeVersionHintsDismissed(): void {
    this.getVersionHintsDismissed().then((val: string[]) => {
      if (val !== undefined && val !== null){
        this.versionHintsDismissed = val;
      }else{
        this.versionHintsDismissed = [];
      }
    });
  }

}
